import { Services } from "@ca.vmedia.tv/services";
import { VodContentType, OnDemandContent, Genre, ItemPaging, GenreType } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { useApplicationStore } from "@/stores/Application";
import { OnDemandCardData, TabsGenresCardData, FiltersVodCardData, GenreCardData, type ShowAllCardData } from "@/components/Cards/types";
import { useRoute } from 'vue-router';
import { useExtraCards } from "@/composables/useExtraCards";
import { useUserInfoStore } from "@/stores/UserInfo";
import { useGenresStore } from "@/stores/Genres";
import { useVodPageStore } from "@/stores/VodPage";
import type { AxiosError } from 'axios';
import { Logger } from '@/utils';
import type { ExposeInterface } from './types';

export const useLinesVodStore = defineStore('LinesVod', (): ExposeInterface => {
    const VodStore = useVodPageStore();
    const {
        getNewShows,
        getNewMovies,
        getRecommendedMovie,
        getMostPopular,
        getNewReleases,
        getWatchAgain,
        getRecommendedShows,
        getContinueWatching
    } = storeToRefs(VodStore);
    const { clearVodPageByFilter } = VodStore;

    const GenresStore = useGenresStore();
    const {
        getTopGenres,
    } = storeToRefs(GenresStore);

    const { hasShowAll, getCardShowAll } = useExtraCards();
    const route = useRoute();

    const ApplicationStore = useApplicationStore();
    const { getFilterVodParams } = storeToRefs(ApplicationStore);
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isClient,
    } = storeToRefs(UserInfoStoreModule);

    const getChannelCards = computed((): (GenreCardData | ShowAllCardData)[] | null => {
        const topGenres = getTopGenres.value;
   
        if (topGenres === null) {
            return null;
        }

        if (topGenres.length === 0) {
            return [];
        }
        
        const items = topGenres.slice(0, AppConstants.ItemsInLine).map((item) => new GenreCardData(item));
        const paging =  new ItemPaging(AppConstants.ItemsInLine, 1, topGenres.length);
        
        if (hasShowAll(paging)) {
            return [...items, ...getCardShowAll(items, paging)];
        }
        
        return items;
    });

    const {orderBy} = useAppConfig();

    const _internalGenres: Ref<Genre[] | null> = ref(null);
    const fetchInternalGenres = async (): Promise<void> => {
        if (_internalGenres.value !== null) {
            return;
        }
        

        Services.Instance.vod.getGenresInternal().then((genresMap) => {
            const promises: Promise<Genre | null>[] = [];

            for (const [genre] of genresMap) {
                const promise = getGenreContent(genre)
                .then((updatedGenre) => {
                    genre.content = updatedGenre.content;
                    return genre;
                })
                .catch((error) => {
                    Logger.captureError(error);
                    return null;
                });

                promises.push(promise);
            }

            Promise.allSettled(promises).then((results) => {
                const genres: Genre[] = results
                .filter(r => r.status === "fulfilled" && r.value !== null)
                .map(r => (r as PromiseFulfilledResult<Genre>).value);
                
                _internalGenres.value = genres.sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
            }).catch((error) => {
                Logger.captureError(error as AxiosError | Error);
            });
        }).catch((error) => {
            Logger.captureError(error as AxiosError | Error);
        });
    };
    const getGenreContent = (genre: Genre): Promise<Genre> => {
        return new Promise((resolve, reject) => {
            const getVodInternalContent = orderBy.getVodInternalContent;
            const {showType} = getFilterVodParams.value;
            
            Services.Instance.vod.getVodInternalContent(
                VodContentType.GenreSubitems,
                AppConstants.GenreItemsInLine,
                1,
                genre,
                142,
                true,
                showType,
                getVodInternalContent
            ).then((data) => {
                if (data.items) {
                    const updatedGenre = new Genre(genre.id);
                    updatedGenre.content = new OnDemandContent(data.items, null, null, null, data.paging);

                    resolve(updatedGenre);
                } 
            }).catch((e) => {
                reject(e);
            });
        });
    };
    const getInternalGenres = computed((): Genre[] | null => _internalGenres.value);
    const getCardsTabsGenres = computed((): TabsGenresCardData[] | null => {
        if (_internalGenres.value === null) {
            return null;
        }

        const items: TabsGenresCardData[] = [];

        _internalGenres.value.forEach((genre: Genre, index: number) => {
            const contentItems = new TabsGenresCardData("tabs-genres-card", genre.name, index, String(genre.id), genre.type === GenreType.Premium);
            items.push(contentItems);
        });

        return items;
    });

    const getCardsNewShows = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getNewShows.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsNewMovies = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getNewMovies.value;
        
        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    }); 

    const getCardsRecommendedShows = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        if (!isClient.value) {
            return [];
        } 
        
        const data = getRecommendedShows.value;
        
        if (data === null || data.items === null) {
            return null;
        } else if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));

        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }

        return items;
    });


    const getCardsRecommendedMovies = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        if (!isClient.value) {
            return [];
        }

        const data = getRecommendedMovie.value;

        if (data === null || data.items === null) {
            return null;
        } else if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsContinueWatching = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getContinueWatching.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsMostPopular = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getMostPopular.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsNewReleases = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getNewReleases.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsWatchAgain = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getWatchAgain.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsFilters = computed((): FiltersVodCardData[] | null => {
        const filtersName = [
            {name: "ALL", type: "All"}, 
            {name: "TV_SHOWS", type: "Series"},
            {name: "MOVIES", type: "Movie"}
        ];
        const items = filtersName.map((item, index) => {
            return new FiltersVodCardData("filter-vod-card", item.name, index, item.type);
        });
        
        return items;
    });

    const getCardsGenre = (id: number | null): (OnDemandCardData | ShowAllCardData)[] | null => {
        const filters = route.query;

        if (id === null || getInternalGenres.value === null) {
            return null;
        }
        if (getInternalGenres.value.length === 0) {
            return [];
        }

        const genre = getInternalGenres.value.find((element)=>{
            return element.id == id ?  true :  false;
        });

        let contentItems = null;
        if (genre?.content && genre.content?.items) {
            contentItems = genre.content?.items.map(
                (element) =>
                    new OnDemandCardData(element)
            );
            if (filters && filters.filter && filters.filter !== 'All' && filters.filter !== "null") {
                const updatedGenres = genre.content?.items.filter(item => item.showType === filters.filter);
                contentItems = updatedGenres.map(
                    (element) =>
                        new OnDemandCardData(element)
                );
            }
            
            if (hasShowAll(genre.content.paging) && contentItems.length > 0) {
                return [...contentItems, ...getCardShowAll(contentItems, genre.content.paging)];
            }
        }

        return contentItems;
    };

    const rowsAmount= 2;
    const _counterRows: Ref<number> = ref(AppConstants.VodCounterLines);
    const getCounterLinesVod = computed((): number => _counterRows.value);
    const setCounterLinesVod = (number: number): void => {
        _counterRows.value = number;
    };
    const clearCounterLinesVod = (): void => {
        _counterRows.value = AppConstants.VodCounterLines;
    };
    const increaseCounterLinesVod = (): void => {
        _counterRows.value += rowsAmount;
    };
    const clearLinesVodStoreByFilter = (): void => {
        clearVodPageByFilter();
        _internalGenres.value = null;
        _counterRows.value = Math.ceil(_counterRows.value / 2) + rowsAmount;
    };

    const clearAllLinesVodStore = (): void => {
        clearCounterLinesVod();
        _internalGenres.value = null;
    };

    return { 
        fetchInternalGenres,
        getInternalGenres,
        getCardsTabsGenres,
        getCardsNewShows,
        getCardsNewMovies,
        getCardsRecommendedShows,
        getCardsRecommendedMovies,
        getCardsContinueWatching,
        getCardsMostPopular,
        getCardsNewReleases,
        getCardsWatchAgain,
        getCardsFilters,
        getCounterLinesVod,
        getChannelCards,
        getCardsGenre,
        setCounterLinesVod,
        clearAllLinesVodStore,
        clearCounterLinesVod,
        increaseCounterLinesVod,
        clearLinesVodStoreByFilter
    };

});